import { useRef, useState } from 'react';
import { Box } from '@mui/material';
import _ from 'lodash';
import * as emoji from 'node-emoji';
import classNames from 'classnames';
import sanitizeHtml from 'sanitize-html';
import Linkify from 'linkify-react';
import { Media } from '../../../../generated/graphql';
import { PostMedia } from './PostMedia';

type Props = {
  text: string;
  media: Media[];
  isPostPage: boolean;
};

const CLAMP_TEXT_SIZE_WITH_ATTACHMENT = 270;
const CLAMP_TEXT_SIZE_WITHOUT_ATTACHMENT = 500;

export const PostContent = ({ text, media, isPostPage }: Props) => {
  const textRef = useRef<HTMLDivElement>();
  const hasAttachments = !_.isEmpty(media);
  const canwrappedText =
    (hasAttachments && text.length > CLAMP_TEXT_SIZE_WITH_ATTACHMENT) ||
    text.length > CLAMP_TEXT_SIZE_WITHOUT_ATTACHMENT;
  const [wrappedText, setWrappedText] = useState(canwrappedText);

  const onWrap = (ev: React.MouseEvent<HTMLParagraphElement>) => {
    ev.stopPropagation();
    setWrappedText((prev) => !prev);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      }}
      ref={textRef}
    >
      <div
        className={classNames('relative', {
          'line-clamp-3': wrappedText && hasAttachments,
          'line-clamp-7': wrappedText && !hasAttachments,
        })}
      >
        <Linkify
          options={{
            truncate: 42,
            target: '_blank',
            attributes: {
              onClick: (ev: React.MouseEvent<HTMLDivElement>) => {
                ev.stopPropagation();
              },
            },
          }}
        >
          {emoji
            .emojify(
              sanitizeHtml(text, {
                allowedTags: ['a'],
                disallowedTagsMode: 'escape',
              }),
            )
            .trim()}
        </Linkify>
        {canwrappedText && (
          <p
            className={classNames(
              'bg-white pl-1 cursor-pointer text-purple-1 font-extrabold',
              {
                'absolute right-0 bottom-0 shadow-[-12px_0px_0px_0px_rgba(255,255,255,0.7)]':
                  wrappedText,
                inline: !wrappedText,
              },
            )}
            onClick={onWrap}
            data-testid="wrap-action"
          >
            {wrappedText ? 'Show More' : 'Show Less'}
          </p>
        )}
      </div>
      {hasAttachments && <PostMedia media={media} isPostPage={isPostPage} />}
    </Box>
  );
};
